import type { CartQuoteLine } from "@pufimi/shared";

// One project group in the cart's two-level tree (ADR-0054 §2): a project heads the
// group (its flat pet photo), under which sit its Products as sub-items. Future
// non-custom catalog lines (no project) fall into a single trailing `null` group.
export interface CartProjectGroup {
  // The grouping key — a project id, or `null` for catalog lines with no project.
  projectId: string | null;
  // The group's lines, in the order they appear in the quote.
  lines: CartQuoteLine[];
}

// Groups the server cart-quote lines into the two-level tree the cart UI renders —
// each project heads a group, its Products underneath (ADR-0054 §2). A pure
// derivation over the quote (the browser-local store holds only ids + quantities);
// group order follows first appearance, so the tree is deterministic and stable as
// lines are added/removed. Lines with no project (future catalog items) collect into
// one trailing `null` group.
export function groupCartLinesByProject(lines: CartQuoteLine[]): CartProjectGroup[] {
  const groups: CartProjectGroup[] = [];
  const byKey = new Map<string | null, CartProjectGroup>();
  for (const line of lines) {
    const key = line.projectId;
    let group = byKey.get(key);
    if (!group) {
      group = { projectId: key, lines: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.lines.push(line);
  }
  return groups;
}
